import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';

import { generatePath } from 'react-router-dom';

import {
  Icons,
  LayoutCard,
  Skeleton,
  Stack,
  tokens,
} from '@cast/design-system';
import { useSearchContextClusters } from '@cast/features-cluster-filters';
import { NoData, NoResults } from '@cast/shared-search-ui';
import { Cluster } from '@cast/types';

import { useNavigate } from '@cast/infrastructure-organization';

import { ClusterHeatmap } from './ClusterHeatmap';
import { DashboardSectionError } from './DashboardSectionError';
import { HeatmapLegend } from './HeatmapLegend';
import { HeatmapModeSwitcher } from './HeatmapModeSwitcher';
import { useClusterHeatmapData } from '../hooks/useClusterHeatmapData';
import { type HeatmapViewMode } from '../utils/clusterHeatmap';

const DEFAULT_CLUSTER_PATH_TEMPLATE = '/clusters/:clusterId';

type ClusterHeatmapSectionProps = {
  emptyStateActions?: ReactNode;
  clusterPathTemplate?: string;
};

export const ClusterHeatmapSection = ({
  emptyStateActions,
  clusterPathTemplate = DEFAULT_CLUSTER_PATH_TEMPLATE,
}: ClusterHeatmapSectionProps) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<HeatmapViewMode>('compute');
  const { allClusters, clusters, hasFilteredEmpty } =
    useSearchContextClusters<Cluster>();

  const { heatmapData, hasClustersWithoutHeatmap, isLoading, error, refetch } =
    useClusterHeatmapData({
      mode,
    });

  const handleClusterClick = useCallback(
    (clusterId: string, clickEvent?: MouseEvent<Element>) => {
      navigate(generatePath(clusterPathTemplate, { clusterId }), {
        clickEvent,
      });
    },
    [navigate, clusterPathTemplate]
  );

  const renderHeatmapContent = () => {
    if (isLoading) {
      return <Skeleton data-testid="cluster-heatmap-loading" height={280} />;
    }

    if (allClusters.length === 0) {
      return (
        <NoData
          icon={<Icons.Nut />}
          entity="cluster"
          actions={emptyStateActions}
        />
      );
    }

    if (hasFilteredEmpty || clusters.length === 0) {
      return <NoResults icon={<Icons.Nut />} entity="cluster" />;
    }

    if (heatmapData.length === 0 && hasClustersWithoutHeatmap) {
      return (
        <NoData
          icon={<Icons.Nut />}
          entity="cluster"
          title="No connected clusters"
          body="Disconnected and not-found clusters are hidden from the heat-map. Connect them to see them here."
        />
      );
    }

    return (
      <ClusterHeatmap data={heatmapData} onClusterClick={handleClusterClick} />
    );
  };

  return (
    <LayoutCard
      title="Cluster heatmap"
      headerEndContent={
        <HeatmapModeSwitcher mode={mode} onModeChange={setMode} />
      }
      slotProps={{ content: { sx: { overflow: 'visible' } } }}
      testId="cluster-heatmap-section"
    >
      <DashboardSectionError hasError={!!error} onRetry={refetch}>
        <Stack gap={16}>
          <Stack
            direction="row"
            alignItems="center"
            gap={12}
            flexWrap="wrap"
            sx={{
              borderBottom: 1,
              borderColor: tokens.theme.border.structural.neutralSubtle,
            }}
          >
            <HeatmapLegend mode={mode} />
          </Stack>
          {renderHeatmapContent()}
        </Stack>
      </DashboardSectionError>
    </LayoutCard>
  );
};
